import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Phone, Mail, Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";

const NAV_ITEMS = [
  { type: "link", label: "Services", to: "/services", testId: "nav-services" },
  { type: "link", label: "Pricing", to: "/pricing", testId: "nav-pricing" },
  { type: "link", label: "Blog", to: "/blog", testId: "nav-blog" },
  { type: "anchor", label: "Why Us", id: "why", testId: "nav-why" },
  { type: "anchor", label: "Industries", id: "industries", testId: "nav-industries" },
  { type: "anchor", label: "Book", id: "book", testId: "nav-book" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu whenever route changes
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, location.hash]);

  // Lock scroll while mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  // Header overlays the hero on the home page (light aqua), the dark
  // hero on services/blog index and sub-pages, and switches to a solid
  // light style once scrolled, on blog posts, or when the mobile menu
  // is open. `onLight` = the new home hero variant (transparent, teal
  // text). `onDark` = the older dark-hero variant used elsewhere.
  const isBlogPost = location.pathname.startsWith("/blog/");
  const onLight = isHome && !scrolled && !mobileOpen;
  const onDark = !isHome && !isBlogPost && !scrolled && !mobileOpen;
  const navText = onDark
    ? "text-white/85 hover:text-cyan-300"
    : onLight
    ? "text-teal-900 hover:text-orange-500"
    : "text-slate-700 hover:text-blue-600";
  const emailText = onDark
    ? "text-white/85 hover:text-cyan-300"
    : onLight
    ? "text-teal-900/80 hover:text-orange-500"
    : "text-slate-600 hover:text-blue-600";

  const goToAnchor = (id) => {
    if (isHome) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate(`/#${id}`);
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 w-full transition-colors duration-300 ${
        onDark
          ? "border-b border-white/10 bg-transparent"
          : onLight
          ? "border-b border-slate-200/60 bg-white"
          : "border-b border-slate-200/60 bg-white/85 backdrop-blur-xl"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-12">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-3">
          <Logo size={40} testId="header-logo" onDark={onDark} />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-6 md:flex lg:gap-8">
          {NAV_ITEMS.map((item) =>
            item.type === "link" ? (
              <Link
                key={item.label}
                to={item.to}
                data-testid={item.testId}
                className={`font-body text-sm font-semibold transition-colors ${navText}`}
              >
                {item.label}
              </Link>
            ) : (
              <button
                key={item.label}
                data-testid={item.testId}
                onClick={() => goToAnchor(item.id)}
                className={`font-body text-sm font-semibold transition-colors ${navText}`}
              >
                {item.label}
              </button>
            )
          )}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <a
            href="mailto:info@deepcleanrr.com"
            data-testid="header-email-link"
            className={`hidden items-center gap-2 font-body text-sm font-semibold transition-colors lg:inline-flex ${emailText}`}
          >
            <Mail size={16} strokeWidth={1.8} />
            info@deepcleanrr.com
          </a>
          <a
            href="tel:9102369971"
            data-testid="header-call-btn"
            className="inline-flex h-11 items-center gap-2 rounded-md bg-blue-600 px-3 sm:px-5 font-body text-xs sm:text-sm font-bold tracking-wide text-white transition-colors hover:bg-blue-700"
          >
            <Phone size={16} strokeWidth={2} />
            <span className="hidden xs:inline sm:inline">910-236-9971</span>
            <span className="xs:hidden sm:hidden">Call</span>
          </a>

          {/* Mobile hamburger */}
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            data-testid="mobile-menu-toggle"
            className={`grid h-11 w-11 place-items-center rounded-md border transition-colors md:hidden ${
              onDark
                ? "border-white/25 text-white hover:bg-white/10"
                : "border-slate-300 text-slate-800 hover:border-blue-600 hover:text-blue-600"
            }`}
          >
            {mobileOpen ? <X size={20} strokeWidth={2.2} /> : <Menu size={20} strokeWidth={2.2} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`md:hidden ${mobileOpen ? "block" : "hidden"}`}
        data-testid="mobile-menu"
      >
        <div className="border-t border-slate-200 bg-white">
          <nav className="mx-auto flex max-w-7xl flex-col px-6 py-3">
            {NAV_ITEMS.map((item) =>
              item.type === "link" ? (
                <Link
                  key={item.label}
                  to={item.to}
                  data-testid={`mobile-${item.testId}`}
                  onClick={() => setMobileOpen(false)}
                  className="border-b border-slate-100 py-4 font-body text-base font-bold text-slate-900 transition-colors hover:text-blue-600"
                >
                  {item.label}
                </Link>
              ) : (
                <button
                  key={item.label}
                  data-testid={`mobile-${item.testId}`}
                  onClick={() => {
                    setMobileOpen(false);
                    setTimeout(() => goToAnchor(item.id), 120);
                  }}
                  className="border-b border-slate-100 py-4 text-left font-body text-base font-bold text-slate-900 transition-colors hover:text-blue-600"
                >
                  {item.label}
                </button>
              )
            )}
            <a
              href="mailto:info@deepcleanrr.com"
              data-testid="mobile-header-email-link"
              className="mt-1 inline-flex items-center gap-2 py-4 font-body text-sm font-semibold text-slate-600 transition-colors hover:text-blue-600"
            >
              <Mail size={16} strokeWidth={1.8} />
              info@deepcleanrr.com
            </a>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
